import type { NavDestination, RoleId } from './navigation';

const EVERYONE_BUT_TECHNICIANS: readonly RoleId[] = [
  'OWNER',
  'SALES_MANAGER',
  'CSR_DISPATCHER',
  'SALES_COORDINATOR',
  'SALES_AGENT',
  'ACCOUNTING',
];

/**
 * Every destination the product has, in rail order.
 *
 * Existing routes keep working: a destination groups the routes it owns as views, and the shell
 * draws the destination's views as tabs on those routes, so a page never builds its own switcher.
 * Pinning decides who sees a destination in the rail; whatever else a person may open is one
 * search away, not hidden behind an "Other" menu.
 */
export const DESTINATIONS: readonly NavDestination[] = [
  {
    id: 'today',
    label: 'Today',
    icon: 'calendar',
    pinnedFor: EVERYONE_BUT_TECHNICIANS,
    views: [
      {
        id: 'today',
        label: 'Today',
        paths: ['/'],
        module: 'CORE',
        keywords: ['overview', 'home', 'my work'],
      },
    ],
  },
  {
    id: 'field',
    label: 'Field',
    icon: 'field',
    // Owners as well as technicians: an owner who works jobs, or checks what a technician sees,
    // must be able to find field mode without searching for it.
    pinnedFor: ['OWNER', 'SERVICE_TECHNICIAN'],
    views: [
      {
        id: 'field',
        label: 'Field',
        paths: ['/field'],
        permission: 'JOB_FIELD_EXECUTE',
        module: 'FIELD',
        keywords: ['field mode', 'visits'],
      },
      {
        id: 'my-handoffs',
        label: 'My handoffs',
        paths: ['/replacement-handoff'],
        permission: 'REPLACEMENT_HANDOFF_SUBMIT',
        module: 'CORE',
        keywords: ['technician handoffs', 'replacement handoff'],
      },
    ],
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: 'dispatch',
    pinnedFor: ['OWNER', 'CSR_DISPATCHER', 'SALES_COORDINATOR', 'SALES_AGENT'],
    views: [
      {
        id: 'board',
        label: 'Board',
        paths: ['/dispatch'],
        permission: 'DISPATCH_BOARD_READ',
        module: 'CORE',
        keywords: ['dispatch', 'dispatch board'],
      },
      {
        id: 'map',
        label: 'Map',
        paths: ['/map'],
        permission: 'DISPATCH_BOARD_READ',
        module: 'ROUTING',
        keywords: ['map and routing', 'routing', 'routes'],
      },
      { id: 'jobs', label: 'Jobs', paths: ['/jobs'], permission: 'JOB_READ', module: 'CORE' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: 'customers',
    pinnedFor: [
      'OWNER',
      'SALES_MANAGER',
      'CSR_DISPATCHER',
      'SALES_COORDINATOR',
      'SALES_AGENT',
      'ACCOUNTING',
    ],
    views: [
      {
        id: 'customers',
        label: 'Customers',
        paths: ['/customers'],
        permission: 'CUSTOMER_READ',
        module: 'CORE',
      },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: 'pipeline',
    pinnedFor: ['OWNER', 'SALES_MANAGER', 'CSR_DISPATCHER', 'SALES_COORDINATOR', 'SALES_AGENT'],
    views: [
      {
        id: 'deals',
        label: 'Deals',
        paths: ['/pipeline'],
        permission: 'OPPORTUNITY_READ',
        module: 'CORE',
        keywords: ['pipeline', 'opportunities'],
      },
      {
        id: 'leads',
        label: 'Leads',
        paths: ['/leads'],
        permission: 'LEAD_INTAKE',
        module: 'CORE',
        keywords: ['lead intake'],
      },
      {
        id: 'proposals',
        label: 'Proposals',
        paths: ['/proposals'],
        permission: 'PROPOSAL_READ',
        module: 'CORE',
      },
      {
        id: 'approvals',
        label: 'Pricing approvals',
        paths: ['/proposals/approvals'],
        permission: 'PRICING_EXCEPTION_APPROVE',
        module: 'CORE',
        keywords: ['approvals', 'exceptions'],
      },
      {
        id: 'handoffs',
        label: 'Technician handoffs',
        paths: ['/replacement'],
        permission: 'OPPORTUNITY_READ',
        module: 'CORE',
        keywords: ['replacement', 'replacement review'],
      },
      {
        id: 'installations',
        label: 'Installations',
        paths: ['/handoff'],
        permission: 'OPPORTUNITY_READ',
        module: 'CORE',
        keywords: ['installation handoff', 'sold jobs'],
      },
      {
        id: 'followups',
        label: 'Follow-ups',
        paths: ['/followup'],
        permission: 'OPPORTUNITY_READ',
        module: 'CORE',
        keywords: ['follow-up', 'followup', 'aftercare'],
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: 'invoicing',
    pinnedFor: ['OWNER', 'ACCOUNTING'],
    views: [
      {
        id: 'invoices',
        label: 'Invoices',
        paths: ['/invoices'],
        permission: 'INVOICE_READ',
        module: 'BILLING',
        keywords: ['invoicing', 'payments', 'billing'],
      },
      {
        id: 'payroll',
        label: 'Payroll',
        paths: ['/payroll'],
        permission: 'PAYROLL_READ',
        module: 'PAYROLL',
        keywords: ['pay'],
      },
    ],
  },
  {
    id: 'pricebook',
    label: 'Pricebook',
    icon: 'pricebook',
    pinnedFor: ['OWNER', 'SALES_MANAGER', 'ACCOUNTING', 'SERVICE_TECHNICIAN'],
    views: [
      {
        id: 'catalogue',
        label: 'Catalogue',
        paths: ['/pricebook'],
        permission: 'PRICEBOOK_READ',
        module: 'CORE',
        keywords: ['prices', 'services', 'equipment'],
      },
      {
        id: 'stock',
        label: 'Stock',
        paths: ['/inventory'],
        permission: 'INVENTORY_READ',
        module: 'INVENTORY',
        keywords: ['inventory', 'materials'],
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'reports',
    pinnedFor: ['OWNER', 'SALES_MANAGER'],
    views: [
      {
        id: 'reports',
        label: 'Reports',
        paths: ['/reports'],
        permission: 'SALES_REPORTING_READ',
        module: 'CORE',
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    icon: 'team',
    pinnedFor: ['OWNER', 'SALES_MANAGER', 'CSR_DISPATCHER'],
    views: [
      {
        id: 'team',
        label: 'Team',
        paths: ['/team'],
        permission: 'TEAM_READ',
        module: 'CORE',
        keywords: ['people', 'crew', 'availability'],
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    pinnedFor: ['OWNER', 'SALES_MANAGER', 'CSR_DISPATCHER', 'SALES_COORDINATOR', 'ACCOUNTING'],
    views: [
      {
        id: 'settings',
        label: 'Settings',
        paths: ['/settings'],
        module: 'CORE',
        keywords: ['preferences', 'configuration', 'setup'],
        // Each part of Settings is offered by its own name and opens on its own tab. The
        // capabilities are those the part asks for in features/settings/settingsSections.tsx.
        places: [
          {
            label: 'Workspace',
            to: '/settings?tab=workspace',
            permission: 'USER_MANAGE',
            keywords: ['workspace name', 'time zone', 'currency'],
          },
          {
            label: 'Accounts',
            to: '/settings?tab=team&item=users',
            permission: 'USER_MANAGE',
            keywords: ['users', 'logins', 'reset password', 'unlock', 'sign out devices'],
          },
          {
            label: 'Roles & permissions',
            to: '/settings?tab=team&item=roles',
            permission: 'USER_MANAGE',
            keywords: ['who can do what'],
          },
          {
            label: 'Intake form & SLAs',
            to: '/settings?tab=sales&item=intake',
            permission: ['LEAD_INTAKE', 'OPPORTUNITY_ROUTE'],
            keywords: ['intake', 'public form', 'lead response'],
          },
          {
            label: 'Consultation templates',
            to: '/settings?tab=sales&item=consultation',
            permission: 'OPPORTUNITY_MANAGE',
            keywords: ['discovery questions'],
          },
          {
            label: 'Proposal templates',
            to: '/settings?tab=sales&item=proposals',
            permission: 'PRICING_CONTROLS_MANAGE',
          },
          {
            label: 'Pricing controls',
            to: '/settings?tab=sales&item=pricing',
            permission: 'PRICEBOOK_READ',
            keywords: ['margin targets', 'discount approval', 'pricing policy'],
          },
          {
            label: 'Working hours',
            to: '/settings?tab=operations&item=hours',
            permission: 'DISPATCH_BOARD_MANAGE',
            keywords: ['scheduling interval', 'business hours'],
          },
          {
            label: 'Job types',
            to: '/settings?tab=operations&item=job-types',
            permission: 'JOB_WRITE',
            keywords: ['job defaults'],
          },
          {
            label: 'Map positions',
            to: '/settings?tab=operations&item=geocoding',
            permission: 'DISPATCH_BOARD_MANAGE',
            module: 'ROUTING',
            keywords: ['geocoding', 'addresses on the map'],
          },
          {
            label: 'Tax rate & payment terms',
            to: '/settings?tab=finance',
            permission: 'FINANCE_SETUP_MANAGE',
            keywords: ['finance setup', 'default tax rate', 'tax', 'invoice terms'],
          },
          {
            label: 'Audit trail',
            to: '/settings?tab=audit',
            permission: 'AUDIT_READ',
            keywords: ['history', 'change log', 'who changed'],
          },
          {
            label: 'Sandbox',
            to: '/settings?tab=sandbox',
            permission: 'USER_MANAGE',
            keywords: ['practice workspace', 'sample data'],
          },
          {
            label: 'My account',
            to: '/settings?tab=account',
            keywords: ['password', 'appearance', 'signed-in devices'],
          },
          {
            label: 'My pay',
            to: '/settings?tab=account&item=pay',
            module: 'PAYROLL',
            keywords: ['pay stub', 'wages', 'paycheck'],
          },
          {
            label: 'System status',
            to: '/settings?tab=status',
            keywords: ['health'],
          },
        ],
      },
    ],
  },
];
